// src/frontend/src/App.tsx - WORKING VERSION (reverted from lazy loading)
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ScrollToTop from './components/Common/ScrollToTop';

// Import pages directly (no lazy loading)
import HomePage from './pages/HomePage';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Settings from './pages/Settings';
import Scenarios from './pages/Scenarios';
import Simulation from './pages/Simulation';
import SimulationHistory from './pages/SimulationHistory';
import StarterKits from './pages/StarterKits';
import KitDetail from './pages/KitDetail';
import CustomScenarios from './pages/CustomScenarios';
import CreateCustomScenario from './pages/CreateCustomScenario';
import TeamAdmin from './pages/TeamAdmin';
import PricingPage from './pages/PricingPage';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import About from './pages/About';
import Security from './pages/Security';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancelled';
import LogoDownload from './pages/LogoDownload';


// SEO template pages
import FeaturePage from './pages/FeaturePage';
import SolutionPage from './pages/SolutionPage';
import ResourcePage from './pages/ResourcePage';
import ComparisonPage from './pages/ComparisonPage';
import IndustryPage from './pages/IndustryPage';

// Protected Route Component
const ProtectedRoute: React.FC<{ element: React.ReactNode }> = ({ element }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <div>Loading...</div>;
  }
  
  return isAuthenticated ? <>{element}</> : <Navigate to="/login" />;
};

// 404 Not Found Component
const NotFound: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
    <div className="text-center max-w-md mx-auto px-6">
      <div className="mb-8">
        <h1 className="text-8xl font-bold text-blue-600 mb-4">404</h1>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Page not found</h2>
        <p className="text-xl text-gray-600 mb-8">
          The page you're looking for doesn't exist or has been moved.
        </p>
      </div>
      <div className="space-y-4">
        <Link 
          to="/" 
          className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
        >
          Go to Home
        </Link>
        <div>
          <Link 
            to="/pricing" 
            className="inline-block text-blue-600 hover:text-blue-800 mx-4"
          >
            View Pricing
          </Link>
          <Link 
            to="/contact" 
            className="inline-block text-blue-600 hover:text-blue-800 mx-4"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  </div>
);

const App: React.FC = () => {
  console.log("APP.TSX IS LOADING - NEW VERSION"); // This should appear in browser console
  
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/about" element={<About />} />
          <Route path="/security" element={<Security />} />
          <Route path="/logo-download" element={<LogoDownload />} />

          {/* SEO Routes */}
          <Route path="/features/:featureName" element={<FeaturePage />} />
          <Route path="/solutions/:useCase" element={<SolutionPage />} />
          <Route path="/resources/:resourceType" element={<ResourcePage />} />
          <Route path="/vs/:competitor" element={<ComparisonPage />} />
          <Route path="/industries/:industry" element={<IndustryPage />} />

          {/* Payment Routes */}
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancelled" element={<PaymentCancelled />} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
          <Route path="/settings" element={<ProtectedRoute element={<Settings />} />} />
          <Route path="/learning-tracks" element={<ProtectedRoute element={<StarterKits />} />} />
          <Route path="/learning-tracks/:slug" element={<ProtectedRoute element={<KitDetail />} />} />
          <Route path="/coaching-sessions" element={<ProtectedRoute element={<Scenarios />} />} />
          <Route path="/coaching-sessions/:id" element={<ProtectedRoute element={<Simulation />} />} />
          <Route path="/coaching-history" element={<ProtectedRoute element={<SimulationHistory />} />} />
          <Route path="/custom-scenarios" element={<ProtectedRoute element={<CustomScenarios />} />} />
          <Route path="/custom-scenarios/new" element={<ProtectedRoute element={<CreateCustomScenario />} />} />
          <Route path="/team-admin" element={<ProtectedRoute element={<TeamAdmin />} />} />

          {/* Legacy redirects */}
          <Route path="/starter-kits" element={<Navigate to="/learning-tracks" replace />} />
          <Route path="/simulations" element={<Navigate to="/coaching-sessions" replace />} />

          {/* Temporary redirects for missing pages */}
          <Route path="/help" element={<Navigate to="/contact" replace />} />
          <Route path="/blog" element={<Navigate to="/about" replace />} />
          <Route path="/careers" element={<Navigate to="/about" replace />} />

          {/* 404 */}
          <Route path="*" element={<div>Page not found</div>} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;